/**
 * Shared delivery-date calculation module.
 *
 * Single source of truth for OG Kicks estimated-delivery logic, consumed by both
 * the PDP (custom-delivery-message.js) and the cart drawer (cart-delivery-message.js).
 * Pure date maths only, no DOM access. Exposed on window.OGKicksDelivery.
 */
(() => {
    // Guard against double execution: the module is included by both the product
    // section and the cart drawer snippet, so it can load twice on a PDP.
    if (window.OGKicksDelivery) return;

    const CUTOFF_HOUR = 14;
    const TIMEZONE = 'Europe/London'; // London timezone for cutoff calculations

    // Dispatch working days: Monday to Friday. Delivery working days: Monday to
    // Saturday. (0 = Sunday, 1 = Monday ... 6 = Saturday)
    const DISPATCH_WORKING_DAYS = [1, 2, 3, 4, 5];
    const DELIVERY_WORKING_DAYS = [1, 2, 3, 4, 5, 6];

    // Dispatch delay 0.5 = same-day dispatch. Delivery delay 1 = one day after dispatch.
    const DISPATCH_DELAY_DAYS = 0.5;
    const DELIVERY_DELAY_DAYS = 1;

    // UK bank holidays (England & Wales), format 'YYYY-MM-DD'. Not counted as
    // business days for either dispatch or delivery.
    const HOLIDAYS = [
        // 2026
        '2026-01-01', // New Year's Day
        '2026-04-03', // Good Friday
        '2026-04-06', // Easter Monday
        '2026-05-04', // Early May bank holiday
        '2026-05-25', // Spring bank holiday
        '2026-08-31', // Summer bank holiday
        '2026-12-25', // Christmas Day
        '2026-12-28', // Boxing Day (substitute)
    ];

    const isWorkingDay = (date, workingDays = DISPATCH_WORKING_DAYS) => {
        // Build YYYY-MM-DD from local date parts so it stays aligned with the
        // London wall-clock used to construct the Date (toISOString would shift
        // to UTC and miss holidays for non-UK browsers).
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;

        if (!workingDays.includes(date.getDay())) return false;
        if (HOLIDAYS.includes(dateStr)) return false;
        return true;
    };

    const addWorkingDays = (startDate, daysToAdd, workingDays = DISPATCH_WORKING_DAYS) => {
        const result = new Date(startDate);
        let remainingDays = daysToAdd;

        while (remainingDays > 0) {
            result.setDate(result.getDate() + 1);
            if (isWorkingDay(result, workingDays)) {
                remainingDays--;
            }
        }

        return result;
    };

    const getNextWorkingDay = (fromDate, workingDays = DISPATCH_WORKING_DAYS) => {
        const result = new Date(fromDate);
        result.setDate(result.getDate() + 1);

        while (!isWorkingDay(result, workingDays)) {
            result.setDate(result.getDate() + 1);
        }

        return result;
    };

    const getLondonNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: TIMEZONE }));

    /**
     * Get the starting point for dispatch calculation.
     * - If today is not a business day, use the next business day.
     * - If today is a business day but after the 14:00 London cutoff, use the next.
     * - Otherwise use today.
     */
    const getDispatchStartDate = () => {
        const now = getLondonNow();

        if (!isWorkingDay(now, DISPATCH_WORKING_DAYS)) {
            return getNextWorkingDay(now, DISPATCH_WORKING_DAYS);
        }

        if (now.getHours() >= CUTOFF_HOUR) {
            return getNextWorkingDay(now, DISPATCH_WORKING_DAYS);
        }

        return now;
    };

    const isSameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    /**
     * Whether a delivery date falls on the day after today in London. Drives the
     * next-day copy: it is only shown when the promise is literally tomorrow,
     * which is false after the cutoff, at weekends and around bank holidays.
     */
    const isTomorrow = (date) => {
        const tomorrow = getLondonNow();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return isSameDay(date, tomorrow);
    };

    const DAY_NAMES = ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'];
    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    /** Format a date as "Wed 05 Aug". */
    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        return `${DAY_NAMES[date.getDay()]} ${day} ${MONTH_NAMES[date.getMonth()]}`;
    };

    /**
     * Format a date range, omitting the month on the first date when both share
     * the same month/year: "Thurs 05 - Mon 09 Aug" vs "Thurs 28 Feb - Mon 03 Mar".
     */
    const formatDateRange = (startDate, endDate) => {
        const startDay = String(startDate.getDate()).padStart(2, '0');
        const endDay = String(endDate.getDate()).padStart(2, '0');
        const sameMonth =
            startDate.getMonth() === endDate.getMonth() &&
            startDate.getFullYear() === endDate.getFullYear();

        if (sameMonth) {
            return `${DAY_NAMES[startDate.getDay()]} ${startDay} - ${DAY_NAMES[endDate.getDay()]} ${endDay} ${MONTH_NAMES[endDate.getMonth()]}`;
        }

        return `${DAY_NAMES[startDate.getDay()]} ${startDay} ${MONTH_NAMES[startDate.getMonth()]} - ${DAY_NAMES[endDate.getDay()]} ${endDay} ${MONTH_NAMES[endDate.getMonth()]}`;
    };

    /**
     * Calculate the delivery date from a dispatch date, applying the dispatch
     * delay (whole days only, fractions ignored) then the delivery delay.
     */
    const calculateDeliveryDate = (dispatchDate, dispatchDelay, deliveryDelay) => {
        let result = new Date(dispatchDate);

        const dispatchDelayDays = Math.floor(dispatchDelay);
        if (dispatchDelayDays > 0) {
            result = addWorkingDays(result, dispatchDelayDays, DISPATCH_WORKING_DAYS);
        }

        const totalDeliveryDays = Math.ceil(deliveryDelay);
        result = addWorkingDays(result, totalDeliveryDays, DELIVERY_WORKING_DAYS);

        return result;
    };

    // The provider metafield holds a comma-separated, priority-ordered list of
    // stocked locations (e.g. "London Soho,R2R Fulfilment"). Only the winner
    // (first element) sets the promise; a slower location further down must not
    // override it.
    const getWinner = (provider) =>
        typeof provider === 'string' ? provider.split(',')[0].trim() : '';

    // The guarantee is a store-wide promise, not a logistics one: it does not depend on which
    // location stocks the selected variant. It inherited that dependency from the pickup copy
    // it replaced, which was specific to the London Soho store.
    const GUARANTEE_COPY = '30-Day Money-Back Guarantee';

    // Copy shown when the calculated delivery date is literally tomorrow. Single
    // source of truth so the PDP and the cart drawer cannot drift apart.
    const NEXT_DAY_COPY = 'Order today, receive tomorrow';

    const exactEstimate = (date) => ({
        kind: isTomorrow(date) ? 'tomorrow' : 'exact',
        text: formatDate(date),
        date,
    });

    // A range never promises tomorrow: even when its earliest day is tomorrow,
    // the commitment is the whole window.
    const rangeEstimate = (minDate, maxDate) => ({
        kind: 'range',
        text: formatDateRange(minDate, maxDate),
    });

    /**
     * Provider groups, matched in order as a case-insensitive substring of the
     * winner. `rangeDays` drives the dated estimate (null = next working day);
     * `simplifiedDays` drives the simplified copy (null = next-day wording).
     * Drop 1 is dated as next day but its simplified copy promises 2–3 days, as
     * specified in the delivery messages sheet.
     */
    const PROVIDER_GROUPS = [
        { match: ['london soho', 'brighton 1', 'brighton 2', 'marlowe mayfair'], rangeDays: null, simplifiedDays: null },
        { match: ['r2r'], rangeDays: [1, 2], simplifiedDays: [1, 2] },
        { match: ['drop 1'], rangeDays: null, simplifiedDays: [2, 3] },
        { match: ['zelph', 'drop 2'], rangeDays: [3, 5], simplifiedDays: [3, 5] },
        { match: ['drop 3'], rangeDays: [7, 10], simplifiedDays: [7, 10] },
    ];

    const findProviderGroup = (provider) => {
        const winner = getWinner(provider).toLowerCase();
        if (!winner) return null;
        return PROVIDER_GROUPS.find((group) => group.match.some((value) => winner.includes(value))) || null;
    };

    const estimateForGroup = (group) => {
        const dispatchStartDate = getDispatchStartDate();

        if (!group.rangeDays) {
            return exactEstimate(calculateDeliveryDate(dispatchStartDate, DISPATCH_DELAY_DAYS, DELIVERY_DELAY_DAYS));
        }

        const [minDays, maxDays] = group.rangeDays;
        return rangeEstimate(
            addWorkingDays(dispatchStartDate, minDays, DELIVERY_WORKING_DAYS),
            addWorkingDays(dispatchStartDate, maxDays, DELIVERY_WORKING_DAYS),
        );
    };

    /**
     * Classified delivery estimate for a provider, or null when it is unknown:
     * { kind: 'tomorrow' | 'exact' | 'range', text }. The date maths are
     * unchanged, `kind` only describes the result so each surface can pick its
     * own wording. Wrapping phrasing is left to the consumer.
     */
    const getDeliveryEstimate = (provider) => {
        const group = findProviderGroup(provider);
        return group ? estimateForGroup(group) : null;
    };

    /**
     * Whether the provider delivers on the next delivery working day. True for
     * the tier 1 stores and Drop 1, false for the range providers (R2R included,
     * which is fast but promises up to two days).
     */
    const isNextDayProvider = (provider) => {
        const estimate = getDeliveryEstimate(provider);
        if (!estimate) return false;
        return estimate.kind === 'tomorrow' || estimate.kind === 'exact';
    };

    // ETA copy variants under A/B test. `current` is the dated copy above; `simplified`
    // states a day window instead of a date. The live value comes from the theme setting
    // `eta_copy_variant`, printed by layout/theme.liquid as window.OGKicksEtaVariant.
    const ETA_VARIANTS = Object.freeze({ current: 'current', simplified: 'simplified' });

    // QA override (?eta_variant=simplified), honoured on preview and development themes
    // only so a shared link can never flip the copy on the live theme. Read once at load:
    // a variant change rewrites the URL to ?variant=… and would drop the parameter.
    const ETA_VARIANT_PARAM = 'eta_variant';
    const LIVE_THEME_ROLE = 'main';

    const PREVIEW_ETA_VARIANT =
        window.Shopify?.theme?.role === LIVE_THEME_ROLE
            ? null
            : new URLSearchParams(window.location.search).get(ETA_VARIANT_PARAM);

    const getEtaVariant = () => {
        const requested = PREVIEW_ETA_VARIANT || window.OGKicksEtaVariant;
        return requested === ETA_VARIANTS.simplified ? ETA_VARIANTS.simplified : ETA_VARIANTS.current;
    };

    const MS_PER_DAY = 24 * 60 * 60 * 1000;

    const withinDaysCopy = ([minDays, maxDays]) => `Order today, receive within ${minDays} – ${maxDays} days`;

    const calendarDaysFromToday = (date) => {
        const today = getLondonNow();
        const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return Math.round((startOfDate - startOfToday) / MS_PER_DAY);
    };

    /**
     * Simplified delivery copy for a provider, or null when the simplified variant is
     * not active or the provider is unknown. Next-day providers that cannot deliver
     * tomorrow (after the cutoff, weekends, bank holidays) count the calendar days to
     * the calculated delivery date and show them as a range ending on that day, so the
     * copy is always in days and never a date.
     */
    const getSimplifiedCopy = (provider) => {
        if (getEtaVariant() !== ETA_VARIANTS.simplified) return null;

        const group = findProviderGroup(provider);
        if (!group) return null;
        if (group.simplifiedDays) return withinDaysCopy(group.simplifiedDays);

        const estimate = estimateForGroup(group);
        if (estimate.kind === 'tomorrow') return NEXT_DAY_COPY;
        const daysToDelivery = calendarDaysFromToday(estimate.date);
        return withinDaysCopy([daysToDelivery - 1, daysToDelivery]);
    };

    /**
     * PDP delivery message: the simplified copy when that variant is active and
     * applies, otherwise the next-day copy when the promise is literally tomorrow,
     * otherwise "Order now to receive <strong>{date or range}</strong>".
     * Null when the provider is unknown.
     */
    const calculateDeliveryMessage = (provider) => {
        const estimate = getDeliveryEstimate(provider);
        if (!estimate) return null;

        const simplified = getSimplifiedCopy(provider);
        if (simplified) return simplified;

        if (estimate.kind === 'tomorrow') return NEXT_DAY_COPY;
        return `Order now to receive <strong>${estimate.text}</strong>`;
    };

    window.OGKicksDelivery = {
        getDispatchStartDate,
        calculateDeliveryDate,
        addWorkingDays,
        formatDate,
        formatDateRange,
        GUARANTEE_COPY,
        getDeliveryEstimate,
        isNextDayProvider,
        calculateDeliveryMessage,
        getSimplifiedCopy,
        getEtaVariant,
        ETA_VARIANTS,
        NEXT_DAY_COPY,
        DELIVERY_WORKING_DAYS,
    };
})();
